import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import logger from '../lib/logger';

// Outcome-led message pool — shared across all 1OS engines.
// Declarative, system-truth tone. Not promises, not benefits — state.
const MESSAGES = [
  'One source of truth.',
  'Every movement, recorded.',
  'The ledger is current.',
  'Every outlet, one view.',
  'Nothing moves without a record.',
  'Operations, as they are.',
  'Reconciled, end to end.',
  'Every number, tracked.',
  'All data is live.',
  'The system is complete.',
];

// 1OS official logo (served from public/ so no bundler import needed).
const ONE_OS_LOGO_SRC = '/1os-logo.png';

const AmbientMessage = ({ slow = false }) => {
  const [idx, setIdx] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);

  // Detect prefers-reduced-motion — freeze rotation on first line if set
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const handler = (e) => setReduceMotion(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    // legacy
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  useEffect(() => {
    if (reduceMotion) return; // cycle stops; first line stays
    // Spec: dwell 2500ms desktop / 3500ms mobile
    const ms = slow ? 3500 : 2500;
    const t = setInterval(() => setIdx(i => (i + 1) % MESSAGES.length), ms);
    return () => clearInterval(t);
  }, [slow, reduceMotion]);

  // Layered cross-fade: render all lines, toggle is-visible on current index.
  // Each span animates opacity (+ translateY on desktop) over 300ms.
  return MESSAGES.map((msg, i) => (
    <span
      key={i}
      className={`ambient-msg ${i === idx ? 'is-visible' : ''}`}
      data-testid={i === idx ? 'login-ambient-message' : undefined}
      aria-hidden={i !== idx}
    >
      {msg}
    </span>
  ));
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const { login, isAuthenticated, loading, error: authError, setError, checkAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    logger.info('[Login] Component mounted');
  }, []);

  useEffect(() => {
    const isAuth = isAuthenticated || checkAuthenticated();
    if (isAuth) navigate('/', { replace: true });
  }, [isAuthenticated, navigate, checkAuthenticated]);

  useEffect(() => {
    if (authError) {
      setLocalError(authError);
      setError(null);
    }
  }, [authError, setError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setLocalError('');
    try {
      const result = await login(email, password);
      if (result.success) {
        try {
          navigate(result.navigateTo, { replace: true });
          setTimeout(() => {
            if (window.location.pathname === '/login' || window.location.pathname === '/') {
              window.location.href = '/';
            }
          }, 500);
        } catch {
          window.location.href = '/';
        }
      }
    } catch (err) {
      setLocalError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="login-shell flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" style={{ color: '#F5C518' }} />
          <p className="text-zinc-400 text-sm">Checking authentication…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell" data-testid="login-page">
      {/* Ambient canvas — vignette + amber glow + grid + noise, no splits */}
      <div className="login-ambient" aria-hidden="true">
        <div className="ambient-vignette" />
        <div className="ambient-amber" />
        <div className="ambient-grid" />
        <div className="ambient-grain" />
        {/* Engine mark — giant, near-transparent, part of the canvas */}
        <div className="ambient-engine-mark hidden lg:flex" aria-hidden="true">INV</div>
      </div>

      {/* Desktop: ambient copy — absolutely positioned right-end, vertically
          centered in viewport. z:2 sits above INV watermark (z:1) but below
          the card layer. pointer-events: none lets clicks pass through. */}
      <div className="ambient-copy hidden lg:flex" aria-hidden="false">
        <div className="ambient-copy-inner">
          <AmbientMessage />
        </div>
      </div>

      {/* Main column — card center-left on desktop, stacked center on mobile */}
      <main className="login-main">
        {/* Logo */}
        <div className="login-brand" data-testid="login-brand">
          <img
            src={ONE_OS_LOGO_SRC}
            alt="1OS"
            className="login-brand-logo"
            draggable="false"
          />
          <span className="login-brand-engine" data-testid="login-brand-engine">Inventory</span>
        </div>

        {/* Mobile-only headline — appears above card (spec: Logo → text → card) */}
        <h1 className="login-mobile-headline lg:hidden" data-testid="login-mobile-headline">
          <AmbientMessage slow />
        </h1>

        {/* Floating card */}
        <form onSubmit={handleSubmit} className="login-card" data-testid="login-form" noValidate>
          <div className="login-card-head">
            <h2 className="login-card-title">Sign in</h2>
            <p className="login-card-sub">Authenticated via Control Room</p>
          </div>

          <div className={`login-error ${localError ? 'is-visible' : ''}`} aria-live="polite" data-testid="login-error">
            {localError && (
              <>
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{localError}</span>
              </>
            )}
          </div>

          <div className="login-field">
            <label htmlFor="email">Work email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoComplete="email"
              disabled={submitting}
              data-testid="email-input"
            />
          </div>

          <div className="login-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
              disabled={submitting}
              data-testid="password-input"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="login-submit"
            data-testid="login-submit-button"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Signing in…</span>
              </>
            ) : (
              <span>Sign in</span>
            )}
          </button>
        </form>

        {/* Minimal footer — system state marker only, not tagline */}
        <p className="login-footer" data-testid="login-footer">v1 · Control Room</p>
      </main>
    </div>
  );
};

export default Login;
